import { useEffect, useRef, useState } from 'react';
import { Text, Button, Card, Subtitle1, Switch, } from '@fluentui/react-components';
import { CheckmarkCircleRegular, DismissRegular, ErrorCircleRegular, QuestionRegular } from "@fluentui/react-icons";
import {
    MessageBar,
    MessageBarActions,
    MessageBarTitle,
    MessageBarBody,
    MessageBarGroup,
    MessageBarIntent,
    makeStyles,
    shorthands,
} from "@fluentui/react-components";
import { PublicClientApplication, InteractionRequiredAuthError, AuthError, AuthenticationResult } from "@azure/msal-browser";

import { msalConfigGraph, loginRequestGraph } from '../authConfigGraph';
import { useSignInState } from '../signInState';
import authErrorHandling from '../authErrorHandling';
import useFetch from '../../Hooks/useFetchAPI';
import { FeatureSkeleton } from '../LoadingSkeletons';
import ModuleConfigTable from './ModuleConfigTable';
import AdminLink from './HelperComponents/AdminLink';

const intents: MessageBarIntent[] = ["info", "warning", "error", "success"];

interface NotificationMessage {
    intent: MessageBarIntent;
    id: number;
    message: string;
};

const useStyles = makeStyles({
    messageBarGroup: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...shorthands.overflow("auto"),
    },
});


const ModuleNISTAPI = ({ tenantConfigured }: { tenantConfigured: boolean }) => {
    const styles = useStyles();
    const [authError, setauthError] = useState<string>('');
    const [connectorTestError, setConnectorTestError] = useState<boolean>(false);
    const { signInState } = useSignInState();
    const featureName = 'IntuneReporting';
    const connectorName = 'NIST';
    const counterRef = useRef(0);

    const [featureConfigData, setFeatureConfigData] = useState<NISTAPIFeatureConfigData[] | null>(null);
    const [nistConfigItems, setNISTConfigItems] = useState<ModuleConfigTableProps[]>([]);
    const { fetchAPI: fetchConfig, apiData, isLoading, serverError } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: updateDatabase, apiData: submitData, isLoading: isLoadingSubmit, serverError: serverErrorSubmit } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: testConfig, apiData: testConfigData, isLoading: isLoadingTestConfig, serverError: serverErrorTestConfig } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: enableDataSync, apiData: enableDataSyncData, isLoading: isLoadingEnableDataSync, serverError: serverErrorEnableDataSync } = useFetch({ scope: 'AdminUser' })

    const [messages, setMessages] = useState<NotificationMessage[]>([]);

    const addMessage = (message: string, intent: MessageBarIntent) => {
        const newMessage = { intent, id: counterRef.current++, message };
        setMessages((s) => [newMessage, ...s]);
    };

    const dismissMessage = (messageId: number) =>
        setMessages((s) => s.filter((entry) => entry.id !== messageId)
        );


    useEffect(() => {
        const fetchConfigAsync = async () => {
            await fetchConfig({ url: `admin/getfeatureconfig?featureName=${featureName}`, method: 'GET' });
        };

        fetchConfigAsync()
    }, [signInState.currentCustomerID]);

    const msalInstanceGraph = new PublicClientApplication(msalConfigGraph(process.env.REACT_APP_CLIENT_GRAPH_APP_ID!));

    const handleAppRegConsent = async () => {
        await msalInstanceGraph.initialize();
        await msalInstanceGraph.loginPopup(loginRequestGraph)
            .then(response => {
                // Handle the response
                setauthError('');
            })
            .catch(error => {
                // Handle the error
                console.log('[consentAppReg] Error:', error);
                setauthError(authErrorHandling(error));
                addMessage(authErrorHandling(error), 'error');
            });
    };

    const updateConfiguration = async (key: string, value: string | number | boolean | Date) => {
        await updateDatabase({ url: `admin/updatefeatureconfig?featureName=${featureName}&${key}=${value}`, method: 'PUT' });

        setFeatureConfigData((prev) => {
            if (!prev) return prev; // Return unchanged if prev is falsy
            return prev.map(item =>
                item.hasOwnProperty(key) ? { ...item, [key]: value } : item
            );
        });
        setNISTConfigItems((prev) => {
            if (!prev) return prev; // Return unchanged if prev is falsy
            return prev.map(item =>
                item.hasOwnProperty(key) ? { ...item, [key]: value } : item
            );
        });

        if (key === "NISTAPIEnabled") {
          await enableDataSync({
            url: "admin/enabledatasync",
            method: "POST",
            body: { NISTAPIEnabled: value },
          });
        }
    }

    useEffect(() => {
        if (apiData) {
            testConfig({ url: `admin/testfeatureconfig?featureName=${featureName}&connectorName=${connectorName}`, method: 'GET' });
        }
    }, []);

    const testConfiguration = async () => {
        console.log('[ModuleConfig-NISTConfig] Testing configuration');
        setMessages([]);
        await testConfig({ url: `admin/testfeatureconfig?featureName=${featureName}&connectorName=${connectorName}`, method: 'GET' });
    }

    useEffect(() => {
        if (testConfigData) {
            (async () => {
                console.log('[ModuleConfig-NISTConfig] testResult:', testConfigData);
                if (testConfigData?.result) {
                    console.log('[ModuleConfig-NISTConfig] Configuration test successful');
                    updateConfiguration('NISTStatus', 'Configured');
                    updateConfiguration('NISTStatusMessage', 'Tested Successfully');
                    setConnectorTestError(false);
                }
                else {
                    console.log('[ModuleConfig-NISTConfig] Configuration test failed');
                    updateConfiguration("NISTStatus", "Not Configured");
                    updateConfiguration('NISTStatusMessage', 'Test Failed');
                    updateConfiguration('NISTAPIEnabled', false);
                    setMessages([]);
                    setConnectorTestError(true);
                    switch (testConfigData.error) {
                        case 'HTTP error! status: 401': {
                            addMessage('HTTP error! status: 401 - Unauthorized. Please check your API keys.', 'error');
                            break;
                        }
                        default:
                            addMessage(testConfigData.error !== undefined ? testConfigData.error.toString() : 'Unknown error, please check your API keys.', 'error');
                    }
                }
            })();
        }
    }, [testConfigData]);

    useEffect(() => {
        if (!isLoading && !serverError && apiData) {
            console.log('[ModuleConfig-NISTConfig] apiData', apiData)
            if (apiData && Array.isArray(apiData.result) && apiData.result.length > 0 && (apiData as unknown as { result: NISTAPIFeatureConfigData[] }).result) {
                let featureConfigDataResult: NISTAPIFeatureConfigData[] | null = null;
                let nistAPIConfigItemsResult: ModuleConfigTableProps[] = [];
                featureConfigDataResult = (apiData as unknown as { result: NISTAPIFeatureConfigData[] }).result;
                // console.log('featureConfigDataResult: ', featureConfigDataResult);
                setFeatureConfigData(featureConfigDataResult);

                if (Array.isArray(featureConfigDataResult)) {
                    nistAPIConfigItemsResult = featureConfigDataResult.flatMap(item => ([
                        { key: 'NISTAPIKey', displayName: 'NIST API Key', value: item.NISTAPIKey, secure: true }
                    ]));
                }
                // console.log('IntuneReportingConfigItemsResult:', IntuneReportingConfigItemsResult);
                setNISTConfigItems(nistAPIConfigItemsResult);
            }
            else {
                console.log('[ModuleConfig-NISTConfig] apiData is empty');
                let featureConfigDataResult: NISTAPIFeatureConfigData[] = [{
                    AppFeatureID: 1,
                    NISTAPIEnabled: false,
                    NISTAPIKey: '',
                    SyncSchedule6: '',
                    NISTStatus: '',
                    NISTStatusMessage: '',
                    NISTAPILastSyncEnd: null,
                    NISTAPILastSyncDuration: null,
                }];
                setFeatureConfigData(featureConfigDataResult);
                let NISTConfigItemsResult: ModuleConfigTableProps[] = [
                    { key: 'NISTAPIKey', displayName: 'NIST API Key', value: '', secure: true }
                ];
                setNISTConfigItems(NISTConfigItemsResult);
            }
        }
    }, [isLoading, serverError, apiData]);

    return (
        <div className="NISTConfig" style={{ margin: '20px', maxWidth: '1200px' }}>
            {isLoading && !serverError && !apiData &&
                <Card>
                    <Subtitle1>NIST API Connector Setup</Subtitle1>
                    <FeatureSkeleton />
                </Card>

            }
            {!isLoading && !serverError && featureConfigData && apiData && nistConfigItems &&
                <Card>
                    <Subtitle1>NIST API Connector Setup</Subtitle1>
                    <Text>To use the NIST API, you must request an API Key. Please see the following Information:</Text>
                    <ul>
                        <li>
                            <AdminLink href="https://nvd.nist.gov/developers/start-here#divRequestAnAPIKey" target="_blank">
                                Information on NIST API Keys
                            </AdminLink>
                        </li>
                        <li>
                            <AdminLink href="https://nvd.nist.gov/developers/request-an-api-key" target="_blank">
                                Request an API Key
                            </AdminLink>
                        </li>
                    </ul>
                    <Switch
                        key={"NISTAPIEnabled"}
                        label={"Connector " + (featureConfigData[0].NISTAPIEnabled ? "enabled" : "disabled")}
                        // label="Connector enabled"
                        checked={featureConfigData[0].NISTAPIEnabled}
                        disabled={!(
                            featureConfigData[0]?.NISTAPIKey &&
                            featureConfigData[0]?.NISTStatusMessage === "Tested Successfully" &&
                            tenantConfigured
                        )}
                        onChange={(e) => updateConfiguration("NISTAPIEnabled", e.target.checked)}
                    />

                    <Text>{featureConfigData[0]?.NISTStatus ? `Connector Status: ${featureConfigData[0]?.NISTStatus}` : null}</Text>
                    <Text>{featureConfigData[0]?.NISTStatusMessage ? `Connector Status Message: ${featureConfigData[0]?.NISTStatusMessage}` : null}</Text>
                    <Text>{featureConfigData[0]?.NISTAPILastSyncEnd ? `Connector Last Sync End Date: ${new Date(featureConfigData[0]?.NISTAPILastSyncEnd).toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : `Connector Last Sync End Date: N/A`}</Text>
                    <Text>
                        {featureConfigData[0]?.NISTAPILastSyncDuration == null
                            ? 'Connector Last Sync Duration: N/A'
                            : `Connector Last Sync Duration: ${featureConfigData[0]?.NISTAPILastSyncDuration === 0
                                ? '< 1 Min'
                                : `${featureConfigData[0]?.NISTAPILastSyncDuration} mins`
                            }`}
                    </Text>

                    < ModuleConfigTable<ModuleConfigTableProps[]> data={nistConfigItems} appFeature={featureName} onUpdate={updateConfiguration} />

                    <div style={{ width: '50%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        <Button style={{ width: '100%' }} onClick={testConfiguration}
                            icon={featureConfigData[0].NISTStatusMessage === 'Tested Successfully'
                                ? <CheckmarkCircleRegular style={{ color: 'green' }} />
                                : <ErrorCircleRegular style={{ color: 'red' }} />}
                        >
                            Test Configuration
                        </Button>
                    </div>
                    {/* <br/> */}
                    {connectorTestError &&
                        <MessageBarGroup className={styles.messageBarGroup}>
                            {messages.map(({ intent, id, message }) => (
                                <MessageBar key={`${intent}-${id}`} intent={intent}>
                                    <MessageBarBody>
                                        <MessageBarTitle>Testing Error:</MessageBarTitle>
                                        {message}
                                    </MessageBarBody>
                                    <MessageBarActions
                                        containerAction={
                                            <Button
                                                onClick={() => dismissMessage(id)}
                                                aria-label="dismiss"
                                                appearance="transparent"
                                                icon={<DismissRegular />}
                                            />
                                        }
                                    />
                                </MessageBar>
                            ))}
                        </MessageBarGroup>
                    }
                </Card>
            }
            {serverError &&
                // Put in nice pretty error handling component when it's created
                <>Server Error: {serverError}</>
            }

        </div>
    );

};

export default ModuleNISTAPI;
